import { InfiniteData, useMutation, useQueryClient } from 'react-query';

import { HttpMethod } from '@api/enums';
import { createHttpClient } from '@api/http-client';
import { END_POINTS } from '@constants/api-endpoints';
import { QUERY_KEYS } from '@constants/query-keys';
import { AlertType } from '@enums/alert';
import { showAlert } from '@lib/alert/alert-manager';

import { Label, LabelsResponseType } from '../types';

export const useDeleteLabel = () => {
  const client = createHttpClient();
  const queryClient = useQueryClient();

  // Updates the cached labels list after deletion
  const updateLabelCache = (labelId: number) => {
    queryClient.setQueriesData<InfiniteData<LabelsResponseType | undefined>>(
      {
        predicate: query => query.queryKey[0] === QUERY_KEYS.LABELS.LABEL_LIST,
      },
      cachedLabelsData => {
        if (!cachedLabelsData) return { pages: [], pageParams: [] };

        const updatedLabelsPages = cachedLabelsData.pages.map(page => {
          if (!page) return page;

          const filteredLabelItems = page.items.filter(
            item => item.id !== labelId,
          );

          return {
            ...page,
            items: filteredLabelItems,
          };
        });

        return {
          ...cachedLabelsData,
          pages: updatedLabelsPages,
        };
      },
    );
  };

  const updateBrandAssetsCache = (deletedLabelId: number) => {
    const brandAssetsQueries = queryClient.getQueriesData<InfiniteData<any>>({
      predicate: query =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === QUERY_KEYS.BRAND_LIBRARY.BRAND_ASSETS_LIST,
    });

    brandAssetsQueries.forEach(([queryKey, cachedBrandAssetsData]) => {
      if (!cachedBrandAssetsData?.pages) return;

      const updatedBrandAssetsPages = cachedBrandAssetsData.pages.map(
        ({ items, ...pageRest }) => {
          const updatedAssetItems = items.map(
            (assetItem: { labels: Label[] }) => {
              const filteredLabels = assetItem.labels.filter(
                label => label.id !== deletedLabelId,
              );

              return {
                ...assetItem,
                labels: filteredLabels,
              };
            },
          );

          return {
            ...pageRest,
            items: updatedAssetItems,
          };
        },
      );

      queryClient.setQueryData<InfiniteData<any>>(queryKey, {
        ...cachedBrandAssetsData,
        pages: updatedBrandAssetsPages,
      });
    });
  };

  const mutation = useMutation(
    (labelId: number) =>
      client({
        method: HttpMethod.DELETE,
        url: END_POINTS.LABELS.DELETE_LABEL(labelId),
      }),
    {
      onSuccess: (_, labelId) => {
        showAlert({
          type: AlertType.SUCCESS,
          message: 'Label deleted successfully',
        });

        updateLabelCache(labelId);
        updateBrandAssetsCache(labelId);
      },
      onError: (error: Error) => {
        showAlert({
          type: AlertType.ERROR,
          message: error.message || 'Failed to delete label',
        });
      },
    },
  );

  return mutation;
};
